/**
 * Synthesised sound effects on WebAudio: no asset files, nothing to license, a few kilobytes of code.
 * Every effect is a short envelope over an oscillator or a filtered noise burst. The context is
 * created on the first pointer gesture (mobile browsers refuse to play before one), and a per-effect
 * rate limit keeps a board of ten machine guns at ×3 speed from turning into white noise.
 */
export type Sfx =
  | 'shot_rapid'
  | 'shot_splash'
  | 'shot_burn'
  | 'shot_chain'
  | 'shot_rifle'
  | 'shot_sniper'
  | 'crit'
  | 'melee'
  | 'enemy_die'
  | 'boss_die'
  | 'boss_intro'
  | 'structure_hit'
  | 'structure_break'
  | 'wall_hit'
  | 'wall_low'
  | 'merge'
  | 'buy'
  | 'sell'
  | 'open_rock'
  | 'error'
  | 'perk'
  | 'wave_start'
  | 'wave_clear'
  | 'win'
  | 'lose'
  | 'hero_skill'
  | 'hero_down'
  | 'soldier_spawn'
  | 'coin'
  | 'tap';

const MUTE_KEY = 'holdline.mute';

/** Minimum gap between two plays of the same effect, ms. */
const GAP: Partial<Record<Sfx, number>> = {
  shot_rapid: 70,
  shot_splash: 120,
  shot_burn: 160,
  shot_chain: 120,
  shot_rifle: 90,
  shot_sniper: 150,
  melee: 90,
  enemy_die: 60,
  structure_hit: 120,
  wall_hit: 140,
  coin: 60,
  soldier_spawn: 200,
};

type Wave = OscillatorType;

class SoundEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  /** Music plugs in here: past the effects mute, before the limiter. */
  private musicBus: GainNode | null = null;
  private readyCallbacks: (() => void)[] = [];

  /** Runs once the context is actually running (creation and resume both happen inside a gesture, but resume is async). */
  onReady(cb: () => void): void {
    if (this.ctx && this.ctx.state === 'running') cb();
    else this.readyCallbacks.push(cb);
  }

  private fireReady(): void {
    const cbs = this.readyCallbacks;
    this.readyCallbacks = [];
    for (const cb of cbs) cb();
  }
  private noiseBuf: AudioBuffer | null = null;
  private last = new Map<Sfx, number>();
  private mutedFlag: boolean;
  /** Pack audio for effects: `sfx:<name>` entries of the art manifest, decoded once the context runs. */
  private packBase: string | null = null;
  private packAudio: Record<string, string> = {};
  private files = new Map<Sfx, AudioBuffer | 'loading' | 'none'>();

  constructor() {
    let m = false;
    try {
      m = localStorage.getItem(MUTE_KEY) === '1';
    } catch {
      /* private mode */
    }
    this.mutedFlag = m;
    // Browsers only start audio inside a user gesture; the first tap anywhere unlocks it.
    const unlock = (): void => this.unlock();
    document.addEventListener('pointerdown', unlock, { capture: true, passive: true });
    document.addEventListener('keydown', unlock, { capture: true, passive: true });
  }

  get muted(): boolean {
    return this.mutedFlag;
  }

  /** The shared AudioContext once unlocked by a gesture, else null. */
  context(): AudioContext | null {
    return this.ctx && this.ctx.state === 'running' ? this.ctx : null;
  }

  bus(): GainNode | null {
    return this.musicBus;
  }

  setMuted(m: boolean): void {
    this.mutedFlag = m;
    try {
      localStorage.setItem(MUTE_KEY, m ? '1' : '0');
    } catch {
      /* fine */
    }
    if (this.master) this.master.gain.value = m ? 0 : 1;
  }

  toggle(): boolean {
    this.setMuted(!this.mutedFlag);
    return !this.mutedFlag;
  }

  /**
   * Point the engine at an art pack. Every `sfx:<name>` entry replaces the synthesised effect of that
   * name (names as in `Sfx`): files are fetched and decoded as soon as the context runs, so the first
   * play after the first tap is already the real one. Missing or broken files keep the synth.
   */
  usePack(base: string, audio: Record<string, string> | undefined): void {
    this.packBase = base;
    this.packAudio = audio ?? {};
    this.files.clear();
    this.onReady(() => {
      for (const key of Object.keys(this.packAudio)) if (key.startsWith('sfx:')) this.fileFor(key.slice(4) as Sfx);
    });
  }

  /** The pack's file for an effect once decoded; kicks off the load and returns null until then. */
  private fileFor(name: Sfx): AudioBuffer | null {
    const state = this.files.get(name);
    if (state instanceof AudioBuffer) return state;
    if (state === 'loading' || state === 'none') return null;
    const file = this.packAudio[`sfx:${name}`];
    if (!file || !this.packBase || !this.ctx) {
      if (!file) this.files.set(name, 'none');
      return null;
    }
    const ctx = this.ctx;
    this.files.set(name, 'loading');
    fetch(`${this.packBase}${file}`)
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
      .then((buf) => ctx.decodeAudioData(buf))
      .then((decoded) => this.files.set(name, decoded))
      .catch(() => this.files.set(name, 'none'));
    return null;
  }

  /** Create or resume the context. Safe to call often. */
  unlock(): void {
    try {
      if (!this.ctx) {
        const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return;
        this.ctx = new Ctor();
        this.master = this.ctx.createGain();
        this.master.gain.value = this.mutedFlag ? 0 : 1;
        // A gentle limiter so overlapping bursts never clip.
        const comp = this.ctx.createDynamicsCompressor();
        comp.threshold.value = -12;
        comp.knee.value = 20;
        comp.ratio.value = 6;
        comp.attack.value = 0.002;
        comp.release.value = 0.12;
        this.master.connect(comp);
        comp.connect(this.ctx.destination);
        this.musicBus = this.ctx.createGain();
        this.musicBus.gain.value = 1;
        this.musicBus.connect(comp);
        const len = this.ctx.sampleRate;
        this.noiseBuf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
        const data = this.noiseBuf.getChannelData(0);
        for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
      }
      if (this.ctx.state === 'suspended') {
        this.ctx.resume().then(() => this.fireReady()).catch(() => {});
      } else if (this.ctx.state === 'running') this.fireReady();
      // Some browsers flip to running a little later; the state change is the reliable signal.
      this.ctx.onstatechange = () => {
        if (this.ctx && this.ctx.state === 'running') this.fireReady();
      };
    } catch {
      this.ctx = null;
    }
  }

  /**
   * Play an effect. `pitch` scales every frequency (1 = as designed), `gain` scales loudness.
   * Silently does nothing before the first gesture, while muted, or in a hidden tab.
   */
  play(name: Sfx, opts: { pitch?: number; gain?: number } = {}): void {
    if (this.mutedFlag || !this.ctx || !this.master || document.hidden) return;
    if (this.ctx.state !== 'running') return;
    const now = performance.now();
    const gap = GAP[name];
    if (gap) {
      const prev = this.last.get(name) ?? -1e9;
      if (now - prev < gap) return;
      this.last.set(name, now);
    }
    const p = opts.pitch ?? 1;
    const g = opts.gain ?? 1;
    const t = this.ctx.currentTime;
    // A pack file for this effect: pitch becomes playback rate, gain an envelope, nothing else changes.
    const file = this.fileFor(name);
    if (file) {
      const src = this.ctx.createBufferSource();
      src.buffer = file;
      src.playbackRate.value = p;
      const env = this.ctx.createGain();
      env.gain.value = g;
      src.connect(env);
      env.connect(this.master);
      src.start(t);
      return;
    }
    switch (name) {
      case 'shot_rapid':
        this.noise(t, 0.05, 'highpass', 1800 * p, 0.35 * g);
        this.tone(t, 0.06, 'square', 640 * p, 220 * p, 0.12 * g);
        break;
      case 'shot_splash':
        this.tone(t, 0.18, 'sine', 160 * p, 60 * p, 0.5 * g);
        this.noise(t, 0.12, 'lowpass', 900 * p, 0.3 * g);
        break;
      case 'shot_burn':
        this.noise(t, 0.28, 'bandpass', 700 * p, 0.28 * g, 0.03);
        break;
      case 'shot_chain':
        this.tone(t, 0.14, 'sawtooth', 1400 * p, 300 * p, 0.16 * g);
        this.noise(t, 0.08, 'highpass', 3000 * p, 0.15 * g);
        break;
      case 'shot_rifle':
        this.noise(t, 0.04, 'highpass', 2400 * p, 0.22 * g);
        this.tone(t, 0.05, 'square', 900 * p, 300 * p, 0.08 * g);
        break;
      case 'shot_sniper':
        this.noise(t, 0.16, 'bandpass', 1500 * p, 0.45 * g);
        this.tone(t, 0.2, 'sine', 240 * p, 70 * p, 0.35 * g);
        break;
      case 'crit':
        this.tone(t, 0.12, 'square', 1200 * p, 600 * p, 0.2 * g);
        this.noise(t, 0.12, 'highpass', 3000, 0.2 * g);
        break;
      case 'melee':
        this.noise(t, 0.05, 'bandpass', 500 * p, 0.25 * g);
        this.tone(t, 0.05, 'triangle', 220 * p, 140 * p, 0.15 * g);
        break;
      case 'enemy_die':
        this.tone(t, 0.16, 'square', 520 * p, 90 * p, 0.2 * g);
        this.noise(t, 0.1, 'lowpass', 1200 * p, 0.15 * g);
        break;
      case 'boss_die':
        this.noise(t, 0.7, 'lowpass', 500, 0.7 * g);
        this.tone(t, 0.6, 'sine', 110, 30, 0.6 * g);
        this.tone(t + 0.05, 0.4, 'square', 300, 60, 0.2 * g);
        break;
      case 'boss_intro':
        this.tone(t, 0.5, 'sawtooth', 70, 55, 0.45 * g);
        this.tone(t + 0.25, 0.5, 'sawtooth', 62, 48, 0.45 * g);
        this.noise(t, 0.5, 'lowpass', 300, 0.3 * g, 0.1);
        break;
      case 'structure_hit':
        this.noise(t, 0.07, 'lowpass', 700 * p, 0.3 * g);
        this.tone(t, 0.06, 'triangle', 180 * p, 120 * p, 0.2 * g);
        break;
      case 'structure_break':
        this.noise(t, 0.35, 'lowpass', 600, 0.6 * g);
        this.tone(t, 0.25, 'square', 200, 50, 0.25 * g);
        break;
      case 'wall_hit':
        this.tone(t, 0.14, 'sine', 120 * p, 50 * p, 0.55 * g);
        this.noise(t, 0.09, 'lowpass', 400, 0.35 * g);
        break;
      case 'wall_low':
        this.tone(t, 0.12, 'sine', 90, 80, 0.45 * g);
        this.tone(t + 0.16, 0.12, 'sine', 85, 75, 0.35 * g);
        break;
      case 'merge': {
        // Two-note chime that climbs with the resulting level.
        const base = 440 * p;
        this.tone(t, 0.18, 'triangle', base, base, 0.3 * g);
        this.tone(t + 0.08, 0.28, 'triangle', base * 1.5, base * 1.5, 0.3 * g);
        this.tone(t + 0.08, 0.28, 'sine', base * 3, base * 3, 0.08 * g);
        break;
      }
      case 'buy':
        this.tone(t, 0.08, 'triangle', 520 * p, 700 * p, 0.25 * g);
        this.noise(t, 0.04, 'highpass', 2500, 0.1 * g);
        break;
      case 'sell':
      case 'coin':
        this.tone(t, 0.06, 'square', 1200 * p, 1200 * p, 0.12 * g);
        this.tone(t + 0.06, 0.14, 'square', 1600 * p, 1600 * p, 0.12 * g);
        break;
      case 'open_rock':
        this.noise(t, 0.22, 'lowpass', 900, 0.4 * g);
        this.tone(t, 0.12, 'triangle', 150, 70, 0.3 * g);
        break;
      case 'error':
        this.tone(t, 0.12, 'square', 220, 200, 0.18 * g);
        this.tone(t + 0.13, 0.16, 'square', 180, 160, 0.18 * g);
        break;
      case 'perk':
        this.arp(t, [523, 659, 784, 1046], 0.07, 0.22, 'triangle', 0.25 * g);
        break;
      case 'wave_start':
        this.tone(t, 0.25, 'sawtooth', 196, 196, 0.25 * g);
        this.tone(t + 0.22, 0.4, 'sawtooth', 262, 262, 0.25 * g);
        this.noise(t, 0.1, 'lowpass', 500, 0.3 * g);
        break;
      case 'wave_clear':
        this.arp(t, [392, 494, 587, 784], 0.08, 0.3, 'triangle', 0.28 * g);
        break;
      case 'win':
        this.arp(t, [392, 494, 587, 784, 988, 1175], 0.1, 0.5, 'triangle', 0.3 * g);
        this.tone(t + 0.6, 0.9, 'sine', 587, 587, 0.15 * g);
        break;
      case 'lose':
        this.arp(t, [392, 349, 311, 262], 0.22, 0.5, 'sawtooth', 0.22 * g);
        this.noise(t, 0.6, 'lowpass', 300, 0.25 * g, 0.2);
        break;
      case 'hero_skill':
        this.noise(t, 0.3, 'bandpass', 1200, 0.35 * g, 0.02);
        this.tone(t, 0.3, 'sine', 300, 80, 0.4 * g);
        break;
      case 'hero_down':
        this.tone(t, 0.4, 'triangle', 330, 110, 0.3 * g);
        this.noise(t, 0.25, 'lowpass', 800, 0.2 * g);
        break;
      case 'soldier_spawn':
        this.tone(t, 0.07, 'triangle', 700 * p, 900 * p, 0.1 * g);
        break;
      case 'tap':
        this.tone(t, 0.04, 'square', 900, 900, 0.08 * g);
        break;
    }
  }

  /** One oscillator with an exponential pitch slide and a percussive envelope. */
  private tone(at: number, dur: number, type: Wave, f0: number, f1: number, gain: number, attack = 0.004): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(Math.max(20, f0), at);
    if (f1 !== f0) osc.frequency.exponentialRampToValueAtTime(Math.max(20, f1), at + dur);
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, at);
    env.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), at + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    osc.connect(env);
    env.connect(this.master!);
    osc.start(at);
    osc.stop(at + dur + 0.02);
  }

  /** A burst of filtered white noise. */
  private noise(at: number, dur: number, filter: BiquadFilterType, freq: number, gain: number, attack = 0.003): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf!;
    src.loop = true;
    const flt = ctx.createBiquadFilter();
    flt.type = filter;
    flt.frequency.value = Math.max(40, freq);
    flt.Q.value = filter === 'bandpass' ? 0.9 : 0.7;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, at);
    env.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), at + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    src.connect(flt);
    flt.connect(env);
    env.connect(this.master!);
    src.start(at);
    src.stop(at + dur + 0.02);
  }

  private arp(at: number, notes: number[], step: number, tail: number, type: Wave, gain: number): void {
    notes.forEach((f, i) => this.tone(at + i * step, i === notes.length - 1 ? tail : step * 1.6, type, f, f, gain));
  }
}

export const sound = new SoundEngine();
